/**
 * The architectural pivot the demo exists to argue about.
 *
 * Both variants ship the same purchase and invite flows. They diverge the
 * moment a member's device blocks something: who is allowed to know *whose*
 * device it was, and what the backend is permitted to persist to make that
 * possible.
 *
 * Everything downstream (alert copy, member detail, request targeting, the
 * push text) reads from this config, so switching modes never means forking a
 * screen.
 */

export type ArchitectureId = 'zero-knowledge' | 'mapped'

export type Architecture = {
  id: ArchitectureId
  /** Short name on the switch. */
  name: string
  /** What the model maps to what, shown under the name on the switch. */
  qualifier: string
  tagline: string
  summary: string

  /** What the backend persists under this model. */
  serverStores: string[]
  /** What it deliberately never learns — the no-logs commitment, made concrete. */
  serverNeverStores: string[]
  strengths: string[]
  costs: string[]

  // ── behavioural switches ──────────────────────────────────────────
  /**
   * May an alert be traced back to the person who owns the device?
   *
   * Both models deliver the same alert, naming the same handset. The question
   * is only whether anything links that handset to a member.
   */
  attributesAlerts: boolean
  /** May the manager enumerate a member's devices? */
  exposesMemberDevices: boolean
  /** Sharing is requested per device, or per person. */
  requestGranularity: 'device' | 'member'
  /**
   * Can the manager respond to a specific member about a specific alert?
   * This is the hinge for any future "reacting" feature — see README.
   */
  supportsDirectedReactions: boolean
  /**
   * Can the manager see that a specific device switched a protection off, and
   * turn it back on remotely? Needs the device to be addressable, which is
   * exactly what the zero-knowledge model refuses to store.
   */
  supportsRemoteFeatureControl: boolean
  /**
   * Can a member's account-level alert — a dark web breach — reach the manager?
   *
   * The match is on an email rather than a handset, but delivering it still
   * means telling the manager something about a named member. Zero knowledge
   * pairs devices, never accounts, so it has no channel for this at all.
   */
  sharesAccountAlerts: boolean

  // ── copy that changes per model ───────────────────────────────────
  copy: {
    /** Anonymised stand-in for a device name in alert rows. */
    anonymousDevice: string
    /** Manager-side push when a group threat lands. */
    pushPrefix: string
    /** Consent prompt shown on the member's handset. */
    consentBody: string
    /** What the member is told the manager will see. */
    consentVisibility: string
    /** Manager-side label on a member who is sharing. */
    sharingLabel: string
    notSharingLabel: string
    /** Button that asks a member/device to start sharing. */
    requestLabel: string
    /** Explains the request before it is sent. */
    requestBody: string
    /** Heading above the sharing controls on the member detail screen. */
    sharingSectionLabel: string
    /** Group-alerts setup screen: what the manager is choosing between. */
    groupShareHeading: string
    groupShareIntro: string
    groupSharePrivacyNote: string
  }
}

export const ARCHITECTURES: Record<ArchitectureId, Architecture> = {
  'zero-knowledge': {
    id: 'zero-knowledge',
    name: 'Zero Knowledge',
    qualifier: 'Device to Device',
    tagline: 'No user↔device mapping',
    summary:
      'The group is a billing and entitlement construct only. Devices pair with each other directly and send the alerts they raise to whichever device they paired with — nothing records which member any handset belongs to.',
    serverStores: [
      'Group → member accounts (seats, invite state, billing)',
      'Device-to-device pairings, held against blinded tokens — not accounts',
    ],
    serverNeverStores: [
      'Which device belongs to which member',
      'Which member a blocked threat came from',
      'Alerts once relayed between the two paired devices — nothing is retained',
      'Any history it could aggregate into scores, stats or a group posture',
    ],
    strengths: [
      'The no-logs claim survives contact with the group feature',
      'A breach or subpoena yields no per-person threat history',
      'Consent is simple: two devices pair, or they do not',
      'Any device in the group can pair with any other; membership and sharing stay separate concerns',
    ],
    costs: [
      'No "your son nearly clicked this" moment — the emotional hook is weaker',
      'Manager cannot help a specific member who is being targeted',
      'No way to see, or restore, a protection someone switched off',
      'No history to aggregate, so security scores, stats and posture are off the table',
      'Every sharing pair needs its own handshake — the group cannot switch it on for you',
      'Account-level alerts have nowhere to go: pairing joins devices, never accounts, so a dark web breach stays on the member\u2019s own phone',
    ],
    attributesAlerts: false,
    exposesMemberDevices: false,
    requestGranularity: 'member',
    supportsDirectedReactions: false,
    supportsRemoteFeatureControl: false,
    sharesAccountAlerts: false,
    copy: {
      anonymousDevice: 'A device in your group',
      pushPrefix: 'A threat was blocked in your group',
      consentBody:
        'This device would send the threats it blocks to the device it is paired with. They see what was blocked — never anything tying this handset to you.',
      consentVisibility: 'Blocked threats only · not linked to your account',
      sharingLabel: 'Supervised device',
      notSharingLabel: 'Not sharing alerts',
      requestLabel: 'Ask to pair for alerts',
      requestBody:
        'Whoever holds it decides, on the device. You would see what it blocks — never which member the handset belongs to.',
      sharingSectionLabel: 'Group contribution',
      groupShareHeading: 'Devices in your group',
      groupShareIntro:
        'Devices in the group are listed on their own — nothing records which member each one belongs to. Pair with one to receive the threats it blocks; whoever holds it approves on the device.',
      groupSharePrivacyNote:
        "You'll see what a paired device blocked and when. You won't see whose device it is, or anything they browse.",
    },
  },

  mapped: {
    id: 'mapped',
    name: 'Device Map',
    qualifier: 'Device to User',
    tagline: 'User↔device inside the group',
    summary:
      'The group stores which devices belong to which member, so a blocked threat can be attributed. The manager sees that Vito’s iPhone was targeted, and can act on it — at the cost of holding a mapping that did not previously exist.',
    serverStores: [
      'Group → member accounts (seats, invite state, billing)',
      'Member → device registry, scoped to the group',
      'Event-level blocked-threat records, attributed to a device',
    ],
    serverNeverStores: [
      'Browsing history or traffic content',
      'Anything from devices that have not opted in',
      'Attributed events once a member leaves the group',
    ],
    strengths: [
      'The demo lands: a named device, a real threat, a reason to care',
      'Manager can help the specific person being targeted',
      'Protections switched off can be spotted and restored remotely',
      'No pairing step — devices belong to a member, and alerts reach every device the manager owns',
      'Per-account history makes security scores, group stats and posture possible (not built here)',
      'Account-level alerts can reach the manager — a dark web breach names the member it belongs to',
      'Foundation for reactions, nudges and per-member security scores',
    ],
    costs: [
      'Creates a user↔device mapping that no-logs marketing has to be squared with',
      'Consent has to be per device, revocable, and visibly so',
      'Membership and sharing are entangled: joining the group is what makes a device addressable',
      'Breach or legal request now exposes per-person threat history',
    ],
    attributesAlerts: true,
    exposesMemberDevices: true,
    requestGranularity: 'device',
    supportsDirectedReactions: true,
    supportsRemoteFeatureControl: true,
    sharesAccountAlerts: true,
    copy: {
      anonymousDevice: 'A device in your group',
      pushPrefix: '',
      consentBody:
        'Michael would see the threats this device blocks — the type and the time. Never the sites you visit, your calls, your messages or their content.',
      consentVisibility: 'Blocked threats only · never content',
      sharingLabel: 'Supervised device',
      notSharingLabel: 'Not sharing alerts',
      requestLabel: 'Ask to share alerts',
      requestBody:
        'They decide on their own device. You would see the threat type and time — never sites, calls, messages or content.',
      sharingSectionLabel: 'Known devices',
      groupShareHeading: 'Devices in your group',
      groupShareIntro:
        "Ask a member's device to tell you when it blocks something. They approve it, and can switch it off anytime.",
      groupSharePrivacyNote:
        "You'll see what was blocked and when. You won't see anything they browse, message or download.",
    },
  },
}

export const ARCHITECTURE_IDS = Object.keys(ARCHITECTURES) as ArchitectureId[]

/** Full name for prose and documentation, e.g. "Device Map (Device to User)". */
export const architectureLabel = (a: Architecture) => `${a.name} (${a.qualifier})`

export const DEFAULT_ARCHITECTURE: ArchitectureId = 'mapped'
